import {
  Component,
  ElementRef,
  viewChild,
  WritableSignal,
  signal,
  inject,
  Signal,
  effect,
  computed,
  input,
  InputSignal,
  DestroyRef,
  Injector,
  afterNextRender,
} from '@angular/core';
import { Chart, Plugin } from 'chart.js/auto';
import type { ActiveElement, ChartEvent, ScriptableScaleContext } from 'chart.js';
import { ColorService } from '../../services/color.service';
import { MediaBlockService } from '../../services/media-block-service';
import { Coordinate, TimerPipe } from '@kinomap-dev/ng-ui-lib/core';

const SCALE_FONT_FAMILY: string = "'Inter', sans-serif";
const SCALE_FONT_SIZE: number = 11;
const CHART_AREA_RADIUS: number = 8;
/** Gouttière des libellés d'altitude, que le parent doit réserver à droite de la piste. */
export const ELEVATION_SCALE_WIDTH: number = 56;

@Component({
  selector: 'app-syncro-editor-slope-visualizer',
  imports: [TimerPipe],
  template: `
    @defer (when mediaBlockService.controlsDone()) {
      <div class="chart-wrapper">
        <canvas #slopeChart></canvas>
      </div>
      @if (showElevationScale() && elevationRange() && trackHeight()) {
        <div
          class="elevation-scale"
          [style.width.px]="elevationScaleWidth()"
          [style.height.px]="trackHeight()"
          [style.color]="scaleTickColor()"
        >
          <span>{{ elevationRange()!.max }} m</span>
          <span>{{ elevationRange()!.min }} m</span>
        </div>
      }
      @if (selection()) {
        <div
          class="selection"
          [style.width]="selectionWidthStyle()"
          [style.left]="selectionLeftStyle()"
        >
          <div class="times">
            <span class="start">{{ selection()!.start | timer: selection()!.end }}</span>
            <span class="end">{{ selection()!.end | timer: selection()!.end }}</span>
          </div>
        </div>
      }
      @if (hasUncoveredTrack()) {
        <div
          class="empty-data"
          [style.width]="trackWidthStyle()"
          [style.height.px]="trackHeight()"
        ></div>
      }
    }
    @if (showSkeleton()) {
      <div class="skeleton">
        <div class="skeleton-track" [style.width]="trackWidthStyle()">
          <p class="placeholder">
            @if (mediaBlockService.gpsLoading()) {
              analyse du tracé GPS en cours ...
            } @else {
              Ajoutez un tracé GPS
            }
          </p>
        </div>
        @if (showElevationScale()) {
          <div class="skeleton-scale" [style.width.px]="elevationScaleWidth()">
            <span>100 m</span>
            <span class="middle">50 m</span>
            <span>0 m</span>
          </div>
        }
      </div>
    }
  `,
  host: {
    class: 'slope-graph',
    '[style.--out-selection-opacity]': "selection() ? '0.1' : '0.5'",
    '[style.background]': "hasScale() ? 'transparent' : null",
    '[style.width]': 'hostWidthStyle()',
  },
  styleUrl: './syncro-editor-slope-visualizer.scss',
})
export class SyncroEditorSlopeVisualizer {
  protected mediaBlockService: MediaBlockService = inject(MediaBlockService);
  showElevationScale: InputSignal<boolean> = input(false);
  showDistanceScale: InputSignal<boolean> = input(false);
  hasScale: Signal<boolean> = computed(() => this.showElevationScale() || this.showDistanceScale());
  elevationScaleWidth: Signal<number> = computed(() =>
    this.showElevationScale() ? ELEVATION_SCALE_WIDTH : 0,
  );
  hostWidthStyle: Signal<string | null> = computed(() =>
    this.elevationScaleWidth() ? `calc(100% + ${this.elevationScaleWidth()}px)` : null,
  );
  trackWidthStyle: Signal<string | null> = computed(() =>
    this.elevationScaleWidth() ? `calc(100% - ${this.elevationScaleWidth()}px)` : null,
  );
  /** Tracé absent ou en cours de chargement : le chart n'est pas encore dessiné. */
  showSkeleton: Signal<boolean> = computed(
    () => !this.mediaBlockService.controlsDone() || this.trackHeight() === null,
  );
  hasUncoveredTrack: Signal<boolean> = computed(() => {
    const gpsDuration: number = this.mediaBlockService.gpsFileData()?.duration ?? 0;
    return !!gpsDuration && (this.mediaBlockService.effectiveTotalDuration() ?? 0) > gpsDuration;
  });
  private chart!: Chart;
  slopeChartRef = viewChild<ElementRef<HTMLCanvasElement>>('slopeChart');
  trackHeight: WritableSignal<number | null> = signal(null);
  /** Bornes de l'échelle d'altitude, affichées en HTML en haut et en bas de la piste. */
  elevationRange: WritableSignal<{ min: number; max: number } | null> = signal(null);
  label: WritableSignal<number[] | undefined> = signal(undefined);
  data: WritableSignal<number[] | undefined> = signal(undefined);
  distances: WritableSignal<number[] | undefined> = signal(undefined);
  private colorService: ColorService = inject(ColorService);
  private injector: Injector = inject(Injector);

  canvasPaddingLeft: Signal<number> = computed(() => 8);
  canvasPaddingRight: Signal<number> = computed(() =>
    this.mediaBlockService.videoLongerThanGps() ? 0 : 8,
  );

  selection: Signal<{ start: number; end: number } | undefined> = this.mediaBlockService.selection;

  lineColor: Signal<string> = this.colorService.neutral400;
  chartFillColor: Signal<string> = this.colorService.neutral200;
  chartAreaBackgroundColor: Signal<string> = this.colorService.brandSecondary100;
  scaleTickColor: Signal<string> = this.colorService.neutral400;
  scaleMinorTickColor: Signal<string> = this.colorService.neutral300;

  private trackInset: Signal<number> = computed(
    () => this.canvasPaddingLeft() + this.canvasPaddingRight() + this.elevationScaleWidth(),
  );

  selectionWidthStyle: Signal<string> = computed(() => {
    const videoDuration: number = this.mediaBlockService.videoFileData()?.duration ?? 0;
    const effectiveTotal: number = this.mediaBlockService.effectiveTotalDuration() ?? 0;
    if (!effectiveTotal) return '0%';
    const ratio: number = videoDuration / effectiveTotal;
    const inset: number = this.trackInset();
    return `calc(${this.canvasPaddingLeft() + this.canvasPaddingRight()}px + ${ratio} * (100% - ${inset}px))`;
  });

  selectionLeftStyle: Signal<string> = computed(() => {
    const offset: number = this.mediaBlockService.syncOffsetSeconds();
    const effectiveTotal: number = this.mediaBlockService.effectiveTotalDuration() ?? 0;
    if (!effectiveTotal || !offset) return '0px';
    const ratio: number = offset / effectiveTotal;
    return `calc(${ratio} * (100% - ${this.trackInset()}px))`;
  });

  /** Pas des graduations principales de l'échelle de distance, en km. */
  private majorDistanceStep(maxKm: number): number {
    const steps: number[] = [0.5, 1, 2, 5, 10, 20, 50, 100];
    const rough: number = maxKm / 8;
    return steps.find((step: number) => step >= rough) ?? steps[steps.length - 1];
  }

  private isMajorDistanceTick(value: number, majorStepKm: number): boolean {
    const ratio: number = value / majorStepKm;
    return Math.abs(ratio - Math.round(ratio)) < 1e-6;
  }

  /** Signale à la carte le point du tracé survolé, pour qu'elle y place l'avatar. */
  private setHoveredIndex(index: number | undefined): void {
    const coordinates: Coordinate[] = this.mediaBlockService.syncedCoordinates();
    this.mediaBlockService.hoveredCoordinate.set(
      index === undefined ? undefined : coordinates[index],
    );
  }

  private clearHover = (): void => this.setHoveredIndex(undefined);

  initChart(label: number[], data: number[]): void {
    const canvas: ElementRef<HTMLCanvasElement> | undefined = this.slopeChartRef();
    if (!canvas) return;

    canvas.nativeElement.addEventListener('mouseleave', this.clearHover);

    const distances: number[] | undefined = this.distances();
    const useDistanceScale: boolean =
      this.showDistanceScale() && !!distances && distances.length === data.length;
    const maxKm: number = useDistanceScale ? distances![distances!.length - 1] / 1000 : 0;
    const majorStepKm: number = useDistanceScale ? this.majorDistanceStep(maxKm) : 0;

    const gpsStart: number = label[0];
    const gpsDuration: number = label[label.length - 1] - gpsStart;
    const totalDuration: number = this.mediaBlockService.effectiveTotalDuration() || gpsDuration;
    const trackEndValue: number = useDistanceScale ? maxKm : gpsDuration;

    const axisDecorations = {
      border: { display: false },
      grid: { display: false, drawTicks: false, drawOnChartArea: false },
    };

    const topInset: number = 2;

    const xScale = useDistanceScale
      ? {
          type: 'linear' as const,
          min: 0,
          max: maxKm,
          afterFit: (scale: { paddingLeft: number; paddingRight: number }): void => {
            scale.paddingLeft = 0;
            scale.paddingRight = 0;
          },
          ...axisDecorations,
          ticks: {
            // Une graduation tous les quarts de pas, les intermédiaires rendues en points.
            autoSkip: false,
            stepSize: majorStepKm / 4,
            padding: 10,
            align: 'inner' as const,
            font: { family: SCALE_FONT_FAMILY, size: SCALE_FONT_SIZE },
            color: (context: ScriptableScaleContext): string =>
              this.isMajorDistanceTick(context.tick.value, majorStepKm)
                ? this.scaleTickColor()
                : this.scaleMinorTickColor(),
            callback: (value: number | string): string => {
              const km: number = Number(value);
              return this.isMajorDistanceTick(km, majorStepKm) ? `${+km.toFixed(1)}km` : '·';
            },
          },
        }
      : {
          type: 'linear' as const,
          min: 0,
          max: totalDuration,
          ...axisDecorations,
          ticks: { display: false },
        };

    // Échelle calée sur les altitudes du tracé (arrondies à la dizaine). Les libellés min/max
    // sont rendus en HTML (cf. .elevation-scale), alignés sur les bords de la piste.
    const elevations: number[] = data.filter((elevation: number) => Number.isFinite(elevation));
    const elevationMin: number = elevations.length
      ? Math.floor(Math.min(...elevations) / 10) * 10
      : 0;
    const elevationMax: number = Math.max(
      elevations.length ? Math.ceil(Math.max(...elevations) / 10) * 10 : 0,
      elevationMin + 10,
    );
    this.elevationRange.set({ min: elevationMin, max: elevationMax });

    const yScale = this.showElevationScale()
      ? {
          min: elevationMin,
          max: elevationMax,
          position: 'right' as const,
          afterFit: (scale: { width: number; paddingTop: number; paddingBottom: number }): void => {
            scale.width = ELEVATION_SCALE_WIDTH;
            scale.paddingTop = 0;
            scale.paddingBottom = 0;
          },
          ...axisDecorations,
          ticks: { display: false },
        }
      : {
          beginAtZero: true,
          position: 'left' as const,
          ...axisDecorations,
          ticks: { display: false },
        };

    // La boîte de la piste, calquée sur celle de la vidéo : elle exclut les échelles et
    // la courbe est rentrée à l'intérieur (cf. layout.padding), la bande restante
    // accueillant les bords de la sélection.
    const radius: number = this.hasScale() ? CHART_AREA_RADIUS : 0;
    const trackBox = (chart: Chart): void => {
      chart.ctx.beginPath();
      chart.ctx.roundRect(
        0,
        0,
        chart.scales['x'].getPixelForValue(trackEndValue) + this.canvasPaddingRight(),
        chart.chartArea.bottom,
        radius,
      );
    };

    const chartAreaBackground: Plugin<'line'> = {
      id: 'chartAreaBackground',
      afterLayout: (chart: Chart): void => this.trackHeight.set(chart.chartArea.bottom),
      beforeDraw: (chart: Chart): void => {
        chart.ctx.save();
        chart.ctx.fillStyle = this.chartAreaBackgroundColor();
        trackBox(chart);
        chart.ctx.fill();
        chart.ctx.restore();
      },
      // Pour que le remplissage sous la courbe suive les coins arrondis.
      beforeDatasetsDraw: (chart: Chart): void => {
        chart.ctx.save();
        trackBox(chart);
        chart.ctx.clip();
      },
      afterDatasetsDraw: (chart: Chart): void => {
        chart.ctx.restore();
      },
    };

    this.chart?.destroy();
    this.chart = new Chart(canvas.nativeElement, {
      type: 'line',
      plugins: [chartAreaBackground],
      data: {
        datasets: [
          {
            data: data.map((elevation: number, index: number) => ({
              x: useDistanceScale ? distances![index] / 1000 : label[index] - gpsStart,
              y: elevation,
            })),
            borderWidth: 2,
            pointRadius: 0,
            pointHoverRadius: 0,
            tension: 0.1,
            borderColor: this.lineColor(),
            fill: {
              target: 'start',
              above: this.chartFillColor(),
            },
          },
        ],
      },
      options: {
        maintainAspectRatio: false,
        // Tant qu'une animation tourne, chart.js diffère les resize — et un chart de
        // largeur nulle n'étant jamais dessiné, il ne les rejoue jamais.
        animation: false,
        plugins: {
          tooltip: {
            enabled: false,
          },
          legend: {
            display: false,
          },
        },
        // On ne veut que le point le plus proche en abscisse, sans exiger que le curseur
        // soit pile sur la courbe.
        interaction: {
          mode: 'index',
          intersect: false,
          axis: 'x',
        },
        onHover: (_event: ChartEvent, elements: ActiveElement[]): void =>
          this.setHoveredIndex(elements[0]?.index),
        layout: {
          // Marge de la piste vidéo (`padding-inline: 8px`). Elle doit rester dans la
          // mise en page du chart : un padding CSS sur le canvas compresserait le dessin
          // au lieu de le décaler.
          padding: {
            left: this.canvasPaddingLeft(),
            right: this.canvasPaddingRight(),
            top: topInset,
            bottom: 0,
          },
        },
        scales: {
          x: xScale,
          y: yScale,
        },
      },
    });

    // Construit avant que sa vue ne soit attachée, le chart se mesure à zéro.
    afterNextRender(() => this.chart?.resize(), { injector: this.injector });
  }

  constructor() {
    // Rattrape les conteneurs dimensionnés après coup (modale en cours d'ouverture).
    const host: HTMLElement = inject(ElementRef).nativeElement;
    const resizeObserver: ResizeObserver = new ResizeObserver(() => this.chart?.resize());
    resizeObserver.observe(host);
    inject(DestroyRef).onDestroy(() => {
      resizeObserver.disconnect();
      this.clearHover();
    });

    // Le décalage de synchronisation allonge le bloc : l'axe le suit sans reconstruire le chart.
    effect(() => {
      const total: number | undefined = this.mediaBlockService.effectiveTotalDuration();
      const xScale = this.chart?.options.scales?.['x'] as { max?: number } | undefined;
      if (!xScale || !total || this.showDistanceScale()) return;
      xScale.max = total;
      this.chart.update('none');
    });

    effect(() => {
      const controlsDone = this.mediaBlockService.controlsDone();
      this.data.set(this.mediaBlockService.chartData());
      this.label.set(this.mediaBlockService.chartLabels());
      this.distances.set(this.mediaBlockService.chartDistances());
      const label = this.label();
      const data = this.data();
      if (controlsDone && label && label.length > 0 && data && data.length > 0) {
        this.initChart(label, data);
      } else {
        this.chart?.destroy();
        this.chart = null!;
        this.trackHeight.set(null);
        this.elevationRange.set(null);
        this.clearHover();
      }
    });
  }
}
